import React, { useEffect, useState } from 'react';
import {
    Menu,
    ChevronRight,
    LogOut,
} from 'lucide-react';
import { NavBadge } from './NavBadge';
import { NavLanguageMenu } from './NavLanguageMenu';
import { cn } from 'utils/cn';
import LangService from 'services/LangService';
import {
    NavItemIcon,
    navHairlineClass,
    navInkClass,
    navMutedClass,
    navSurfaceClass,
} from '../navIcons';

const NAV_DRAWER_BODY_CLASS = 'nav-drawer-open';

const isItemActive = (item, activePath) => {
    if (typeof item?.isActive === 'function') {
        return item.isActive(activePath);
    }
    return activePath === item?.to;
};

export const NavMobileDrawer = ({
    items = [],
    activePath = '/my_communities',
    user,
    onNavigate,
    onItemClick,
    onLogout,
    languages,
    selectedLanguage,
    onSelectLanguage,
    languageSlot,
    hideAccountChrome = false,
    className,
    surfaceClassName = navSurfaceClass,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const headerLang = LangService.map.components?.header || {};
    const logoutLang = LangService.map.components?.logout || {};

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        document.body.classList.add(NAV_DRAWER_BODY_CLASS);
        return () => {
            document.body.classList.remove(NAV_DRAWER_BODY_CLASS);
        };
    }, [isOpen]);
    const profileTo = `/u/${user?.slug || 'me'}`;
    const isProfileActive = activePath === '/profile' || activePath === profileTo || activePath === '/u/me';

    const handleNavigate = (to) => {
        setIsOpen(false);
        if (onNavigate) onNavigate(to);
    };

    const handleItemClick = (item) => {
        setIsOpen(false);
        if (onItemClick) {
            onItemClick(item);
            return;
        }
        if (item?.onClick) {
            item.onClick();
            return;
        }
        if (onNavigate && item?.to) {
            onNavigate(item.to);
        }
    };

    return (
        <div className={className}>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className={cn('p-2 rounded-lg hover:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', navInkClass)}
                aria-label="Open mobile menu"
            >
                <Menu className="w-5 h-5" />
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in"
                        onClick={() => setIsOpen(false)}
                    />

                    <div className={cn('relative ml-auto w-full max-w-xs h-full shadow-2xl flex flex-col z-50 animate-in slide-in-from-right duration-250', surfaceClassName)}>
                        {!hideAccountChrome && user && (
                            <button
                                type="button"
                                data-testid="nav-drawer-user"
                                onClick={() => handleNavigate(`/u/${user.slug || 'me'}`)}
                                className={cn(
                                    'p-4 border-b flex items-center justify-between text-left transition-colors',
                                    navHairlineClass,
                                    isProfileActive
                                        ? 'bg-primary text-primary-foreground'
                                        : cn(surfaceClassName, 'hover:bg-primary/10')
                                )}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-border" />
                                    ) : (
                                        <div className={cn(
                                            'w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm',
                                            isProfileActive ? 'bg-primary-foreground/15 text-primary-foreground' : 'bg-primary/10 text-primary'
                                        )}>
                                            {user.initials || 'U'}
                                        </div>
                                    )}
                                    <div className="leading-tight min-w-0">
                                        <p className={cn('text-sm font-bold truncate max-w-[170px]', !isProfileActive && navInkClass)}>{user.name}</p>
                                        {user.email ? (
                                            <p className={cn('text-xs truncate max-w-[170px]', isProfileActive ? 'text-primary-foreground/80' : navMutedClass)}>
                                                {user.email}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                                <ChevronRight className={cn('w-4 h-4 shrink-0', isProfileActive ? 'text-primary-foreground' : navMutedClass)} />
                            </button>
                        )}

                        <div className="flex-1 overflow-y-auto p-3 space-y-1">
                            <p className={cn('px-3 py-1 text-[11px] font-semibold uppercase tracking-wider', navMutedClass)}>
                                {headerLang.navigation || 'Navigation'}
                            </p>
                            {items.map((item) => {
                                const isActive = isItemActive(item, activePath);
                                return (
                                    <button
                                        key={item.id || item.to}
                                        type="button"
                                        onClick={() => handleItemClick(item)}
                                        className={cn(
                                            'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer',
                                            isActive
                                                ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                                                : cn(navInkClass, 'hover:bg-primary/10')
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <NavItemIcon
                                                item={item}
                                                className={isActive ? 'text-primary-foreground' : navMutedClass}
                                            />
                                            <span>{item.label}</span>
                                        </div>
                                        {typeof item.badge === 'string' || typeof item.badge === 'number'
                                            ? (item.badge ? (
                                                <NavBadge variant={isActive ? 'secondary' : (item.badgeVariant || 'primary')}>
                                                    {item.badge}
                                                </NavBadge>
                                            ) : null)
                                            : item.badge}
                                    </button>
                                );
                            })}
                        </div>

                        <div className={cn('p-4 border-t flex items-center justify-between', navHairlineClass, surfaceClassName)}>
                            {languageSlot !== undefined ? languageSlot : (
                                <NavLanguageMenu
                                    languages={languages}
                                    selectedLanguage={selectedLanguage}
                                    onSelectLanguage={onSelectLanguage}
                                />
                            )}

                            {!hideAccountChrome && (user ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsOpen(false);
                                        if (onLogout) onLogout();
                                    }}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-destructive hover:opacity-80 transition-opacity"
                                >
                                    <LogOut className="w-3.5 h-3.5" />
                                    <span>{headerLang.logOut || 'Log out'}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => handleNavigate('/login')}
                                    className="text-xs font-semibold text-primary hover:underline"
                                >
                                    {logoutLang.in || 'Log in'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
